import { useState } from "react";
import {
  Button,
  Dropdown,
  Popconfirm,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { MenuProps } from "antd";
import {
  LoadingOutlined,
  PlayCircleOutlined,
  MoreOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { sharePodcast, revokePodcastShare } from "@vojomo-core/shared";
import type { Podcast } from "@vojomo-core/shared";

interface PodcastCardProps {
  podcast: Podcast;
  onPlay: (podcast: Podcast) => void;
  onRegenerate: (podcast: Podcast) => void;
  onDelete: (podcastId: string) => void;
  onUpdate: (updated: Partial<Podcast> & { _id: string }) => void;
}

const TYPE_LABELS: Record<string, string> = {
  deep_dive: "Deep Dive",
  summary: "Summary",
  analysis: "Analysis",
  interview: "Interview",
  briefing: "Briefing",
};

const LENGTH_LABELS: Record<string, string> = {
  brief: "~3 min",
  standard: "~8 min",
  deep: "~15 min",
};

const SOURCE_LABELS: Record<string, string> = {
  qbr_scoring: "QBR Scoring",
  weekly_progress: "Weekly Progress",
  objectives: "Objectives",
  roadmap_building_blocks: "Roadmap",
  custom_text: "Custom",
  platform_document: "Document",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export const PodcastCard = ({
  podcast,
  onPlay,
  onRegenerate,
  onDelete,
  onUpdate,
}: PodcastCardProps) => {
  const [sharing, setSharing] = useState(false);

  const isGenerating = podcast.status === "generating" || podcast.status === "pending";
  const isFailed = podcast.status === "failed";
  const isReady = podcast.status === "ready";

  const sourceLabels = [
    ...new Set(podcast.data_sources.map((s) => SOURCE_LABELS[s.type] || s.type)),
  ];

  const handleShare = async () => {
    setSharing(true);
    try {
      const result = await sharePodcast(podcast._id);
      await navigator.clipboard.writeText(result.share_url);
      onUpdate({ _id: podcast._id, share_token: result.token });
      message.success("Share link copied to clipboard");
    } catch {
      message.error("Failed to create share link");
    } finally {
      setSharing(false);
    }
  };

  const handleRevokeShare = async () => {
    try {
      await revokePodcastShare(podcast._id);
      onUpdate({ _id: podcast._id, share_token: undefined });
      message.success("Share link revoked");
    } catch {
      message.error("Failed to revoke share link");
    }
  };

  const handleDownload = () => {
    const url = `/podcast/${podcast._id}/download`;
    window.open(url, "_blank");
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "share",
      icon: <ShareAltOutlined />,
      label: sharing ? "Copying link…" : podcast.share_token ? "Copy share link" : "Share externally",
      onClick: handleShare,
      disabled: !isReady || sharing,
    },
    podcast.share_token
      ? {
          key: "revoke",
          icon: <LinkOutlined />,
          label: "Revoke share link",
          onClick: handleRevokeShare,
          danger: true,
        }
      : null,
    {
      key: "download",
      icon: <DownloadOutlined />,
      label: "Download MP3",
      onClick: handleDownload,
      disabled: !isReady,
    },
    { type: "divider" },
    {
      key: "regenerate",
      icon: <ReloadOutlined />,
      label: "Regenerate",
      onClick: () => onRegenerate(podcast),
      disabled: isGenerating,
    },
  ].filter(Boolean) as MenuProps["items"];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">🎙</span>
            <span className="font-semibold text-gray-900 truncate">{podcast.title}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <span>{TYPE_LABELS[podcast.podcast_type] || podcast.podcast_type}</span>
            <span>·</span>
            <span>{LENGTH_LABELS[podcast.podcast_length] || podcast.podcast_length}</span>
            <span>·</span>
            <span>{formatDate(podcast.created_at)}</span>
          </div>
          {podcast.created_by_name && (
            <div className="text-xs text-gray-400">by {podcast.created_by_name}</div>
          )}
        </div>

        {/* More menu */}
        <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
          <Button icon={<MoreOutlined />} type="text" size="small" />
        </Dropdown>
      </div>

      {/* Source tags */}
      <div className="flex flex-wrap gap-1">
        {sourceLabels.map((label) => (
          <Tag key={label} color="blue" className="text-xs m-0">
            {label}
          </Tag>
        ))}
        {podcast.share_token && (
          <Tooltip title="Share link is active">
            <Tag icon={<LinkOutlined />} color="green" className="text-xs m-0">
              Shared
            </Tag>
          </Tooltip>
        )}
      </div>

      {/* Status */}
      {isGenerating && (
        <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
          <LoadingOutlined spin />
          <span>Vo is creating your podcast…</span>
        </div>
      )}
      {isFailed && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          <ExclamationCircleOutlined />
          <span>Generation failed</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-auto pt-1">
        {isReady && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => onPlay(podcast)}
            size="small"
          >
            Play
          </Button>
        )}
        {(isFailed || isReady) && (
          <Tooltip title="Regenerate">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => onRegenerate(podcast)}
              size="small"
            />
          </Tooltip>
        )}
        <Popconfirm
          title="Delete this podcast?"
          description="This cannot be undone."
          onConfirm={() => onDelete(podcast._id)}
          okText="Delete"
          okType="danger"
          cancelText="Cancel"
        >
          <Tooltip title="Delete">
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Tooltip>
        </Popconfirm>
      </div>
    </div>
  );
};
